import React, { useEffect, useRef, useState, useCallback } from 'react';

// UI AudioPlayer Icons
const IconPlay = () => (
 <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
   <polygon points="5,3 19,12 5,21" />
 </svg>
);

const IconPause = () => (
 <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
   <rect x="5" y="3" width="4" height="18" rx="1" />
   <rect x="15" y="3" width="4" height="18" rx="1" />
 </svg>
);

const IconRewind = () => (
 <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
   <path d="M1 4v6h6" />
   <path d="M3.51 15a9 9 0 1 0 .49-5.5" />
 </svg>
);

const IconForward = () => (
 <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
   <path d="M23 4v6h-6" />
   <path d="M20.49 15a9 9 0 1 1-.49-5.5" />
 </svg>
);

const IconClose = () => (
 <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
   <path d="M18 6L6 18M6 6l12 12" />
 </svg>
);

// Audio Waves
const Waveform: React.FC<{ playing: boolean }> = ({ playing }) => {
 const bars = [3,5,8,6,10,7,12,9,14,11,8,13,10,15,11,9,13,10,7,12,8,11,6,9,4,7,5,3];

 return (
   <div
     className="waveform"
     aria-hidden
     style={{
       display: 'flex',
       alignItems: 'center',
       gap: '2px',
       height: '28px',
       padding: '0 4px',
     }}
   >
     {bars.map((h, i) => (
       <div
         key={i}
         className={playing ? 'bar bar--playing' : 'bar'}
         style={{
           width: '3px',
           height: `${h}px`,
           borderRadius: '2px',
           backgroundColor: 'var(--color-accent)',
           animationDelay: playing ? `${(i * 37) % 400}ms` : '0ms',
           transition: 'height 0.15s ease',
         }}
       />
     ))}
   </div>
 );
};

export interface AudioPlayerProps {
 audioElement: HTMLAudioElement | null;
 onClose?: () => void;
 className?: string;
}

// Audio Player: used within AudioPlayerPopout
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
 audioElement,
 onClose,
 className = '',
}) => {
 const [isPlaying, setIsPlaying] = useState(false);
 const [currentTime, setCurrentTime] = useState(0);
 const [playbackRate, setPlaybackRate] = useState(1);

 useEffect(() => {
   const el = audioElement;
   if (!el) return;

   const onPlay    = () => setIsPlaying(true);
   const onPause   = () => setIsPlaying(false);
   const onEnded   = () => setIsPlaying(false);
   const onTime    = () => setCurrentTime(el.currentTime);

   el.addEventListener('play',             onPlay);
   el.addEventListener('pause',            onPause);
   el.addEventListener('ended',            onEnded);
   el.addEventListener('timeupdate',       onTime);

   setIsPlaying(!el.paused);
   setCurrentTime(el.currentTime);
   setPlaybackRate(el.playbackRate);

   return () => {
     el.removeEventListener('play',            onPlay);
     el.removeEventListener('pause',           onPause);
     el.removeEventListener('ended',           onEnded);
     el.removeEventListener('timeupdate',      onTime);
   };
 }, [audioElement]);

 const togglePlay = useCallback(() => {
   const el = audioElement;
   if (!el) return;
   if (el.paused) el.play();
   else el.pause();
 }, [audioElement]);

 const skip = useCallback((seconds: number) => {
   const el = audioElement;
   if (!el) return;
   el.currentTime = Math.max(0, Math.min(el.duration || Infinity, el.currentTime + seconds));
 }, [audioElement]);

 const cycleSpeed = useCallback(() => {
   const el = audioElement;
   if (!el) return;
   const rates = [1, 1.5, 2];
   const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length];
   el.playbackRate = next;
   setPlaybackRate(next);
 }, [audioElement, playbackRate]);

 const formatTime = (t: number) => {
   if (!isFinite(t)) return '00:00';
   const m = Math.floor(t / 60);
   const s = Math.floor(t % 60);
   return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
 };

 return (
   <>
     <style>{`
       @keyframes wavePulse {
         0%, 100% { transform: scaleY(1);   opacity: 0.7; }
         50%       { transform: scaleY(1.7); opacity: 1;   }
       }
       .bar--playing {
         animation: wavePulse 0.8s ease-in-out infinite;
       }
       .audio-pill button {
         cursor: pointer;
         border: none;
         background: none;
         color: inherit;
         padding: 0;
       }
       .audio-pill button:focus-visible {
         outline: 2px solid var(--color-accent);
         outline-offset: 2px;
         border-radius: 6px;
       }
     `}</style>

     <div
       className={`audio-pill ${className}`}
       role="region"
       aria-label="Audio player"
       style={{
         display: 'inline-flex',
         alignItems: 'center',
         gap: '6px',
         background: 'var(--color-surface)',
         border: '1px solid var(--color-accent-soft)',
         color: 'var(--color-text)',
         borderRadius: '999px',
         padding: '10px 16px 16px',
         boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
         userSelect: 'none',
         minWidth: 0,
       }}
     >
       <button
         onClick={togglePlay}
         aria-label={isPlaying ? 'Pause' : 'Play'}
         style={{
           width: 36,
           height: 36,
           borderRadius: '50%',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           background: 'var(--color-accent)',
           color: 'var(--color-text-inverse)',
           flexShrink: 0,
         }}
       >
         {isPlaying ? <IconPause /> : <IconPlay />}
       </button>
       <span
         style={{
           fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
           fontSize: '13px',
           fontWeight: 500,
           letterSpacing: '0.02em',
           minWidth: '38px',
           textAlign: 'center',
           flexShrink: 0,
           color: 'var(--color-text)',
         }}
       >
         {formatTime(currentTime)}
       </span>

       <Waveform playing={isPlaying} />

       <div style={{ width: 1, height: 20, background: 'var(--color-accent-soft)', flexShrink: 0 }} />

       <button
         onClick={cycleSpeed}
         aria-label={`Playback speed: ${playbackRate}×`}
         style={{
           fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
           fontSize: '12px',
           fontWeight: 600,
           background: 'var(--color-hover)',
           color: 'var(--color-text)',
           borderRadius: '6px',
           padding: '3px 7px',
           flexShrink: 0,
           whiteSpace: 'nowrap',
         }}
       >
         {playbackRate}×
       </button>

        <button
          onClick={() => skip(-15)}
          aria-label="Rewind 15 seconds"
          title="−15s"
          style={{
            width: 34,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-hover)',
            color: 'var(--color-text)',
            flexShrink: 0,
            gap: 1,
          }}
        >
          <IconRewind />
          <span style={{
            fontSize: '8px',
            fontFamily: 'ui-monospace, monospace',
            fontWeight: 700,
            color: 'var(--color-text-secondary)',
            lineHeight: 1,
          }}>15</span>
        </button>

        <button
          onClick={() => skip(15)}
          aria-label="Forward 15 seconds"
          title="+15s"
          style={{
            width: 34,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-hover)',
            color: 'var(--color-text)',
            flexShrink: 0,
            gap: 1,
          }}
        >
          <IconForward />
          <span style={{
            fontSize: '8px',
            fontFamily: 'ui-monospace, monospace',
            fontWeight: 700,
            color: 'var(--color-text-secondary)',
            lineHeight: 1,
          }}>15</span>
        </button>

       {onClose && (
         <>
           <div style={{ width: 1, height: 20, background: 'var(--color-accent-soft)', flexShrink: 0 }} />
           <button
             onClick={onClose}
             aria-label="Close audio player"
             style={{
               width: 30,
               height: 30,
               borderRadius: '50%',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               flexShrink: 0,
               color: 'var(--color-text-secondary)',
             }}
           >
             <IconClose />
           </button>
         </>
       )}
     </div>
   </>
 );
};

export interface AudioPlayerPopoutProps {
 audioElement: HTMLAudioElement | null;
 defaultOpen?: boolean;
 onClose?: () => void;
}

// AudioPlayerPopout: can view and hide on toggle
export const AudioPlayerPopout: React.FC<AudioPlayerPopoutProps> = ({
 audioElement,
 defaultOpen = true,
 onClose,
}) => {
 const [open, setOpen] = useState(defaultOpen);
 const pillRef = useRef<HTMLDivElement>(null);

 const handleClose = useCallback(() => {
   setOpen(false);
   onClose?.();
 }, [onClose]);

 useEffect(() => {
   const handler = (e: MouseEvent) => {
     if (!open) return;
     if (pillRef.current && !pillRef.current.contains(e.target as Node)) handleClose();
   };
   document.addEventListener('mousedown', handler);
   return () => document.removeEventListener('mousedown', handler);
 }, [open, handleClose]);

 return (
   <div
     style={{
       position: 'fixed',
       bottom: 100,
       left: '50%',
       transform: 'translateX(-50%)',
       zIndex: 9999,
     }}
   >
     {!open && (
       <button
         onClick={() => setOpen(true)}
         aria-label="Open audio player"
         style={{
           display: 'flex',
           alignItems: 'center',
           gap: 8,
           padding: '9px 18px',
           borderRadius: '999px',
           background: 'var(--color-surface)',
           border: '1px solid var(--color-accent-soft)',
           color: 'var(--color-text)',
           cursor: 'pointer',
           fontSize: '14px',
           fontWeight: 500,
           boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
         }}
       >
         <IconPlay />
         Play
       </button>
     )}

     <div
       ref={pillRef}
       style={{
         transition: 'opacity 0.18s ease, transform 0.18s ease',
         opacity: open ? 1 : 0,
         transform: open ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.97)',
         pointerEvents: open ? 'auto' : 'none',
         position: open ? 'static' : 'absolute',
       }}
     >
       <AudioPlayer
         audioElement={audioElement}
         onClose={handleClose}
       />
     </div>
   </div>
 );
};

export default AudioPlayerPopout;
